<script lang="ts" setup>
import { faLanguage, type IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import type { Locale } from "~/models/Localization";

export interface LanguageSelectorProps {
    label?: string;
    allowsEmpty?: boolean;
    noneLabel?: string;
    disabledLocales?: Array<any>;
    allowedLocales?: Array<any>;
    defaultLocaleCode?: string;
    compact?: boolean;
    size?: "sm" | "2xs" | "xs" | "md" | "lg" | "xl";
    withIcon?: boolean;
    hideIfSingle?: boolean;
    ui?: any
}

const props = withDefaults(defineProps<LanguageSelectorProps>(), {
    label: "Language",
    allowsEmpty: false,
    noneLabel: "None",
    disabledLocales: () => [],
    size: "md",
    defaultLocaleCode: undefined,
    ui: undefined
})

const modelValue = defineModel<Locale | number | { id?: number | null } | null>("modelValue", {
    required: true,
});

const { data } = useAsyncData("locales", async () => {
    const { data } = await useApi().localization.locales({
        query: {
            pagination: 'none'
        }
    }).catch(() => {
        return { data: [] };
    })

    return data
}, {
    default: () => []
})

watch(data, () => {
    if (!modelValue.value && props.defaultLocaleCode) {
        modelValue.value = data.value.find(
            (locale: Locale) => locale.code === props.defaultLocaleCode,
        ) ?? null;
    }
})

const defaultLocale = computed(() => {
    return data.value.find(
        (locale: Locale) => locale.code === props.defaultLocaleCode,
    ) ?? null;
})

const options = computed(() => {
    let options = data.value;

    if (props.disabledLocales?.length) {
        options = options.map(locale => ({
            ...locale,
            disabled: props.disabledLocales.includes(locale.id) || props.disabledLocales.includes(locale.code)
        }))
    }

    if (props.allowedLocales) {
        options = options.filter(locale => props.allowedLocales.includes(locale.id) || props.allowedLocales.includes(locale.code))
    }

    return useUniqBy(options, 'id');
});

const value = computed({
    get: () => Number.isInteger(modelValue.value) ? modelValue.value : modelValue.value?.id ?? null,
    set: (value) => {
        modelValue.value = value ? options.value.find(option => option.id === value) ?? null : null
    }
})
</script>

<template>
    <UFormGroup v-if="!props.hideIfSingle || options.length > 1" :label="compact ? undefined : props.label" :size="props.size" :ui="ui">
        <UISelectMenu v-model="value" :options="options" searchable
            :none="props.allowsEmpty ? props.noneLabel ?? defaultLocale?.name : undefined" :none-value="null" :size="props.size"
            value-attribute="id" option-attribute="name" :search-attributes="['name', 'code']">
            <template #leading v-if="props.withIcon">
                <FontAwesomeIcon :icon="faLanguage" fixed-width />
            </template>
        </UISelectMenu>
    </UFormGroup>
</template>
